// ComplaintsAdminPage.jsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography,
  Box,
  Tab,
  IconButton,
  Tooltip,
  Modal,
  Paper,
  TextField,
  Button,
  Autocomplete
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { useTranslation } from 'react-i18next';
import ConfirmDeleteDialog from 'src/components/dialogs/delete/ConfirmDeleteDialog';
import {
  StyledTabs,
  TabsWrapper
} from 'src/components/styledHelpComponents/styledHelpComponents';
import { DataGrid } from '@mui/x-data-grid';
import { useLocaleText } from 'src/helper/localeText';
import { cyrillicToLatin, latinToCyrillic } from 'src/helper/helperFunction';
import CloseIcon from '@mui/icons-material/Close';
import { API_BASE_URL } from 'src/apiConfig';
import { StyledSearch, StyledStudentName } from './helper';

/* ADMIN STRANA ZA ZALBE */

const ComplaintsAdminPage = () => {
  const { t } = useTranslation();
  const [complaints, setComplaints] = useState([]);
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [selectedComplaint, setSelectedComplaint] = useState(null);
  const [adminResponse, setAdminResponse] = useState('');
  const [tabIndex, setTabIndex] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [openModal, setOpenModal] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [deleteComplaintId, setDeleteComplaintId] = useState(null);
  const [pageSize, setPageSize] = useState(10);
  const localeText = useLocaleText();
  const [complaintThread, setComplaintThread] = useState([]);

  // New state variables for schools
  const [schoolsWithComplaints, setSchoolsWithComplaints] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState(null);

  const filterByTab = (complaint) => {
    if (tabIndex === 0)
      return (
        complaint.admin_procitao == 0 &&
        complaint.last_sender !== 'Administrator'
      );
    if (tabIndex === 1)
      return (
        complaint.admin_procitao == 1 &&
        complaint.last_sender !== 'Administrator'
      );
    return false;
  };

  useEffect(() => {
    const fetchComplaints = async () => {
      const response = await axios.get(`${API_BASE_URL}/api/complaints.php`, {
        params: { grouped: true }
      });
      setComplaints(response.data);

      // Extract unique schools from complaints
      const uniqueSchools = [
        ...new Set(response.data.map((complaint) => complaint.school_name))
      ];
      setSchoolsWithComplaints(uniqueSchools);

      setFilteredComplaints(response.data.filter(filterByTab));
    };
    fetchComplaints();
  }, [tabIndex]);

  useEffect(() => {
    setSearchQuery('');
    setSelectedSchool(null);
  }, [tabIndex]);

  useEffect(() => {
    if (openModal) {
      setTimeout(() => {
        const messagesContainer = document.getElementById('messagesContainer');
        if (messagesContainer) {
          messagesContainer.scrollTop = messagesContainer.scrollHeight;
        }
      }, 100);
    }
  }, [openModal, complaintThread]);

  useEffect(() => {
    let results = complaints;

    // Filter by search query
    if (searchQuery) {
      const searchString = searchQuery.toLowerCase();
      const searchLatin = cyrillicToLatin(searchString);
      const searchCyrillic = latinToCyrillic(searchString);

      results = results.filter((complaint) => {
        return (
          (complaint.student_name &&
            (cyrillicToLatin(complaint.student_name.toLowerCase()).includes(
              searchLatin
            ) ||
              latinToCyrillic(complaint.student_name.toLowerCase()).includes(
                searchCyrillic
              ))) ||
          (complaint.school_name &&
            (cyrillicToLatin(complaint.school_name.toLowerCase()).includes(
              searchLatin
            ) ||
              latinToCyrillic(complaint.school_name.toLowerCase()).includes(
                searchCyrillic
              )))
        );
      });
    }

    // Filter by selected school
    if (selectedSchool) {
      results = results.filter(
        (complaint) => complaint.school_name === selectedSchool
      );
    }

    const filteredByTab = results.filter(filterByTab);

    setFilteredComplaints(filteredByTab);
  }, [searchQuery, selectedSchool, complaints, tabIndex]);

  const decodeHtmlEntities = (text) => {
    const element = document.createElement('div');
    if (text) {
      element.innerHTML = text;
      return element.textContent;
    }
    return text;
  };

  const fetchComplaintThread = async (id_skole, id_ucenika) => {
    const response = await axios.get(`${API_BASE_URL}/api/complaints.php`, {
      params: {
        thread: true,
        id_skole: id_skole,
        id_ucenika: id_ucenika
      }
    });
    setComplaintThread(response.data);
  };

  const handleOpenModal = async (complaint) => {
    setSelectedComplaint(complaint);

    await fetchComplaintThread(complaint.id_skole, complaint.id_ucenika);

    setOpenModal(true);
  };

  const handleSendResponse = async () => {
    try {
      const today = Math.floor(Date.now() / 1000);
      const updateData = { admin_procitao: 1 };

      await axios.put(
        `${API_BASE_URL}/api/complaints.php?admin_read_id=${selectedComplaint.id}`,
        updateData
      );
      const newComplaint = {
        ...selectedComplaint,
        poruku_poslao: 'Administrator',
        poruka: adminResponse,
        status: 0,
        skola_procitao: 0,
        admin_procitao: 1,
        date: today
      };

      await axios.post(`${API_BASE_URL}/api/complaints.php`, newComplaint);

      setComplaints((prevComplaints) => {
        const updatedComplaints = prevComplaints.map((complaint) =>
          complaint.id_skole === selectedComplaint.id_skole &&
          complaint.id_ucenika === selectedComplaint.id_ucenika
            ? { ...complaint, admin_procitao: 1 }
            : complaint
        );
        return updatedComplaints;
      });

      setFilteredComplaints((prevFiltered) => {
        const updatedFiltered = prevFiltered.map((complaint) =>
          complaint.id_skole === selectedComplaint.id_skole &&
          complaint.id_ucenika === selectedComplaint.id_ucenika
            ? { ...complaint, admin_procitao: 1 }
            : complaint
        );
        return updatedFiltered.filter(filterByTab);
      });

      setComplaintThread((prevThread) => [...prevThread, newComplaint]);

      setAdminResponse('');
    } catch (error) {
      console.error('Error sending response:', error);
    }
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setAdminResponse('');
  };

  const handleOpenConfirmDialog = (complaintId) => {
    setDeleteComplaintId(complaintId);
    setOpenConfirmDialog(true);
  };

  const handleCloseConfirmDialog = () => {
    setOpenConfirmDialog(false);
    setDeleteComplaintId(null);
  };

  const handleAcceptComplaint = () => {
    const studentId = selectedComplaint.id_ucenika;
    //     const message = `Poštovani,
    // vaša žalba na rad učenika ID:${studentId} je uvažena, greška je ispravljena.
    // Srdačan pozdrav,
    // administracija takmičenja "Kengur bez granica"`;
    const message = `Поштовани, Ваша жалба на рад ученика ID:${studentId} је уважена, грешка је исправљена.
Срдачан поздрав,
Комисија такмичења "Кенгур без граница"`;

    setAdminResponse(message);
  };

  const columns = [
    { field: 'id', headerName: t('columns.id'), width: 70 },
    {
      field: 'school_name',
      headerName: t('columns.school'),
      width: 200,
      renderCell: (params) => <span>{params.row.school_name}</span>
    },
    {
      field: 'student_name',
      headerName: t('columns.student'),
      width: 200,
      renderCell: (params) => (
        <StyledStudentName onClick={() => handleOpenModal(params.row)}>
          {params.row.student_name}
        </StyledStudentName>
      )
    },
    {
      field: 'class',
      headerName: t('columns.class'),
      width: 100
    },
    {
      field: 'last_date',
      headerName: t('columns.lastMessageDate'),
      width: 150,
      renderCell: (params) =>
        new Date(params.row.last_date * 1000).toLocaleDateString('sr-RS')
    },
    {
      field: 'admin_procitao',
      headerName: t('columns.admin_read'),
      width: 150,
      renderCell: (params) => (
        <Tooltip
          title={
            params.row.admin_procitao === 1
              ? t('status.read')
              : t('status.unread')
          }
        >
          <IconButton>
            {params.row.admin_procitao === 1 ? (
              <CheckCircleIcon color="success" />
            ) : (
              <RadioButtonUncheckedIcon color="disabled" />
            )}
          </IconButton>
        </Tooltip>
      )
    },
    {
      field: 'actions',
      headerName: t('columns.actions'),
      width: 150,
      renderCell: (params) => (
        <>
          <IconButton onClick={() => handleOpenModal(params.row)}>
            <VisibilityIcon color="info" />
          </IconButton>
          <IconButton onClick={() => handleOpenConfirmDialog(params.row.id)}>
            <DeleteIcon color="error" />
          </IconButton>
        </>
      )
    }
  ];

  const handleSearch = (e) => {
    const searchValue = e.target.value.toLowerCase();
    setSearchQuery(searchValue);
  };

  const handleDeleteComplaint = async () => {
    try {
      await axios.delete(
        `${API_BASE_URL}/api/complaints.php?id=${deleteComplaintId}`
      );
      setComplaints((prevComplaints) =>
        prevComplaints.filter((complaint) => complaint.id !== deleteComplaintId)
      );
      setFilteredComplaints((prevFiltered) =>
        prevFiltered.filter((complaint) => complaint.id !== deleteComplaintId)
      );
      handleCloseConfirmDialog();
    } catch (error) {
      console.error('Error deleting complaint:', error);
    }
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        {t('complaints.title')}
      </Typography>

      <TabsWrapper>
        <StyledTabs
          value={tabIndex}
          onChange={(e, newValue) => setTabIndex(newValue)}
          aria-label={t('results.tabsLabel')}
          sx={{
            backgroundColor: '#f5f7fa',
            borderRadius: '8px',
            boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            marginBottom: '0px'
          }}
        >
          <Tab label={t('tabs.unanswered_complaints')} />
          <Tab label={t('tabs.finished_complaints')} />
        </StyledTabs>
      </TabsWrapper>

      {/* Search and Autocomplete */}
      <Box my={3} display="flex" gap={2}>
        <StyledSearch
          label={t('search.placeholder')}
          variant="outlined"
          value={searchQuery}
          onChange={handleSearch}
        />

        <Autocomplete
          options={schoolsWithComplaints}
          value={selectedSchool}
          onChange={(event, newValue) => setSelectedSchool(newValue)}
          getOptionLabel={(option) => option || ''}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('review.school')}
              variant="outlined"
              sx={{
                backgroundColor: '#fff',
                borderRadius: '8px'
              }}
            />
          )}
          noOptionsText={t('pagination.noRowsLabel')}
          sx={{ width: '250px', height: '80%' }}
        />
      </Box>

      <Box style={{ height: 600, width: '100%', backgroundColor: '#ffff' }}>
        <DataGrid
          rows={filteredComplaints}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20]}
          localeText={localeText}
          pagination
          disableColumnMenu
          componentsProps={{
            pagination: {
              showFirstButton: true,
              showLastButton: true,
              labelRowsPerPage: t('pagination.rowsPerPage'),
              labelDisplayedRows: ({ from, to, count }) =>
                `${from}-${to} ${t('pagination.of')} ${
                  count !== -1 ? count : `${t('pagination.moreThan')} ${to}`
                }`
            }
          }}
          sx={{
            '& .MuiDataGrid-root': {
              border: 'none',
              backgroundColor: '#ffff',
              borderRadius: '12px',
              padding: '10px',
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)'
            },
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#2596be',
              color: '#ffffff',
              borderBottom: 'none',
              borderRadius: '12px 12px 0 0',
              fontSize: '16px'
            },
            '& .MuiDataGrid-cell': {
              borderBottom: 'none',
              padding: '12px',
              fontSize: '14px',
              color: '#333'
            },
            '& .MuiDataGrid-footerContainer': {
              backgroundColor: '#1c75bc',
              color: '#ffffff',
              borderTop: 'none',
              borderRadius: '0 0 12px 12px'
            },
            '& .MuiDataGrid-footerContainer .MuiTypography-root': {
              color: '#ffffff'
            },
            '& .MuiDataGrid-footerContainer .MuiIconButton-root': {
              color: '#ffffff'
            },
            '& .MuiDataGrid-footerContainer .MuiSelect-icon': {
              color: '#ffffff'
            },
            '& .MuiDataGrid-footerContainer .MuiInputBase-input': {
              color: '#ffffff'
            },
            '& .MuiDataGrid-footerContainer .MuiTablePagination-actions .MuiIconButton-root':
              {
                color: '#ffffff'
              }
          }}
        />
      </Box>

      <Modal
        open={openModal}
        onClose={handleCloseModal}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          outline: 'none'
        }}
      >
        <Paper
          elevation={4}
          sx={{
            width: '50%',
            maxWidth: '800px',
            padding: '20px',
            backgroundColor: '#fefefe',
            borderRadius: '12px',
            boxShadow: '0px 6px 20px rgba(0, 0, 0, 0.1)',
            position: 'relative'
          }}
        >
          {/* Header - Title and Close Button */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 'bold',
                color: '#333',
                letterSpacing: '0.5px'
              }}
            >
              {t('complaints.complaintDetails')}
            </Typography>
            {/* Dugme Zatvori sa ikonicom X */}
            <IconButton
              onClick={handleCloseModal}
              sx={{
                // backgroundColor: '#e57373',
                color: '#e5737',
                borderRadius: '8px',
                '&:hover': {
                  backgroundColor: '#1976d2'
                }
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Student Information */}
          <Box
            style={{
              backgroundColor: '#e3f2fd',
              padding: '20px',
              borderRadius: '12px',
              marginBottom: '24px',
              display: 'flex',
              justifyContent: 'space-between',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              border: '1px solid #ccc'
            }}
          >
            <Box>
              <Typography variant="body1" style={{ color: '#333' }}>
                {t('columns.student')}:
                <span style={{ fontWeight: 'bold', color: '#1976d2' }}>
                  {selectedComplaint?.student_name}
                </span>
              </Typography>
              <Typography variant="body1" sx={{ color: '#333' }}>
                {t('columns.school')}:
                <span style={{ color: '#1976d2' }}>
                  {selectedComplaint?.school_name}
                </span>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body1" sx={{ color: '#333' }}>
                {t('columns.id')}:
                <span style={{ color: '#1976d2' }}>
                  {selectedComplaint?.id_ucenika}
                </span>
              </Typography>
              <Typography variant="body1" sx={{ color: '#333' }}>
                {t('columns.professor')}:
                <span style={{ color: '#1976d2' }}>
                  {selectedComplaint?.professor}
                </span>
              </Typography>
            </Box>
          </Box>

          {/* Messages Thread */}
          <Box
            id="messagesContainer"
            sx={{
              maxHeight: '350px',
              overflowY: 'auto',
              padding: '10px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              border: '1px solid #ddd',
              marginBottom: '20px'
            }}
          >
            {complaintThread.map((message, index) => (
              <Box
                key={index}
                sx={{
                  padding: '10px',
                  marginBottom: '10px',
                  borderRadius: '8px',
                  backgroundColor:
                    message.poruku_poslao === 'Administrator'
                      ? '#e1f5fe'
                      : '#fafafa',
                  border: '1px solid #ddd'
                }}
              >
                <Box
                  style={{
                    backgroundColor:
                      message.poruku_poslao === 'Administrator'
                        ? '#e1f5fe'
                        : '#fafafa',
                    borderRadius: '8px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: '#1976d2', fontWeight: 'bold' }}
                  >
                    {message.poruku_poslao === 'Administrator'
                      ? t('complaints.administrator')
                      : t('complaints.school')}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#777', fontSize: '12px', textAlign: 'right' }}
                  >
                    {new Date(message.date * 1000).toLocaleString()}
                  </Typography>
                </Box>
                <Typography
                  variant="body1"
                  sx={{ marginTop: '8px', color: '#333' }}
                >
                  {decodeHtmlEntities(message.poruka)}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* Admin Response Input */}
          <TextField
            label={t('columns.admin_response')}
            multiline
            rows={3}
            variant="outlined"
            fullWidth
            value={adminResponse}
            onChange={(e) => setAdminResponse(e.target.value)}
            sx={{
              marginBottom: '20px',
              backgroundColor: '#ffffff',
              borderRadius: '8px',
              boxShadow: 'inset 0px 1px 2px rgba(0, 0, 0, 0.1)'
            }}
          />

          {/* Action Buttons */}
          <Box
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '20px'
            }}
          >
            <Button
              variant="outlined"
              style={{
                color: '#ffff', // Zelena boja
                backgroundColor: '#4caf50',
                borderColor: '#4caf50',
                padding: '10px 20px',
                textTransform: 'none',
                fontSize: '14px',
                borderRadius: '8px'
              }}
              onClick={() =>
                window.open(
                  `/kengur2/#/school/${selectedComplaint.id_skole}`,
                  '_blank'
                )
              }
            >
              {t('complaints.worksButton')}
            </Button>
            <div>
              <Button
                variant="outlined"
                style={{
                  color: '#1976d2',
                  borderColor: '#1976d2',
                  padding: '10px 20px',
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: '8px',
                  marginRight: '10px'
                }}
                onClick={handleAcceptComplaint}
              >
                {t('complaints.acceptComplaint')}
              </Button>
              <Button
                variant="contained"
                style={{
                  backgroundColor: '#1976d2',
                  color: 'white',
                  padding: '10px 20px',
                  textTransform: 'none',
                  fontSize: '14px',
                  borderRadius: '8px'
                }}
                onClick={handleSendResponse}
              >
                {t('complaints.sendResponse')}
              </Button>
            </div>
          </Box>
        </Paper>
      </Modal>

      <ConfirmDeleteDialog
        open={openConfirmDialog}
        onClose={handleCloseConfirmDialog}
        onConfirm={handleDeleteComplaint}
      />
    </div>
  );
};

export default ComplaintsAdminPage;
